import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { Check } from 'react-bootstrap-icons';
import useEngagement from '../../hooks/useEngagement';
import useViewportEngagement from '../../hooks/useViewportEngagement';

interface PublicUserPanelProps {
  userData: any;
  jobTitle: string;
  iNeedAccessToView?: string;
  iOwnTheAccount?: boolean;
  hasFullAccess?: boolean;
  onResumePreview?: () => void;
  onRequestFullAccess?: () => void;
}

const PublicUserPanel: React.FC<PublicUserPanelProps> = ({
  userData,
  jobTitle,
  iNeedAccessToView,
  iOwnTheAccount,
  hasFullAccess = false,
  onResumePreview,
  onRequestFullAccess,
}) => {
  const [showImageModal, setShowImageModal] = useState(false);
  const { onClick } = useEngagement('user-panel');
  useViewportEngagement('user-panel');
  const navigate = useNavigate();

  const handleImageClick = (e: React.MouseEvent) => {
    onClick({
      type: 'field_click',
      field: 'avatar_click',
      coordinates: { x: e.clientX, y: e.clientY },
    });
    setShowImageModal(true);
  };

  const hasCalendarAccess = Boolean(
    iOwnTheAccount ||
      hasFullAccess ||
      iNeedAccessToView === 'NO' ||
      userData?.access_granted ||
      userData?.accessGranted
  );
  const needsCalendarRequest = !hasCalendarAccess;

  const handleCalendarClick = (e: React.MouseEvent) => {
    if (iOwnTheAccount) return;
    onClick({
      type: 'field_click',
      field: 'view_calendar',
      coordinates: { x: e.clientX, y: e.clientY },
    });
    if (needsCalendarRequest) {
      onRequestFullAccess?.();
      return;
    }
    if (userData?.username) {
      navigate(`/u/${userData.username}/calendar`);
    }
  };

  const isVerified = Boolean(
    userData?.isVerified ||
      userData?.verified ||
      userData?.isIdentityVerified ||
      userData?.verificationStatus === 'verified'
  );

  const displayName =
    userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : 'Candidate Name';

  return (
    <>
      <div className="candidate-profile-card">
        <div className="candidate-profile-card-inner">
          <div className="candidate-profile-avatar-wrap">
            {userData?.profileImg ? (
              <img
                src={userData.profileImg}
                alt={displayName}
                className="candidate-profile-avatar"
                onClick={handleImageClick}
              />
            ) : (
              <button
                type="button"
                className="candidate-profile-avatar candidate-profile-avatar-placeholder"
                onClick={handleImageClick}
                aria-label="Open candidate avatar"
                title="Open candidate avatar"
              >
                <span aria-hidden="true">👤</span>
              </button>
            )}
          </div>

          <div className="candidate-profile-name-row">
            <h2 className="candidate-profile-name">{displayName}</h2>
            {isVerified && (
              <span className="candidate-profile-name-badge" aria-label="Verified">
                <Check size={10} />
              </span>
            )}
          </div>

          <p className="candidate-profile-role">{jobTitle || 'Professional Title'}</p>

          {isVerified && (
            <div className="candidate-verified-chip">
              <Check size={12} />
              Verified
            </div>
          )}

          <div className="candidate-profile-actions">
            <button
              type="button"
              className="candidate-secondary-button"
              onClick={handleCalendarClick}
              disabled={Boolean(iOwnTheAccount)}
              aria-disabled={Boolean(iOwnTheAccount)}
              title={
                iOwnTheAccount
                  ? 'Calendar is view-only in profile preview'
                  : needsCalendarRequest
                    ? 'Request access to view calendar availability'
                    : 'View candidate availability'
              }
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {iOwnTheAccount || !needsCalendarRequest
                ? 'View Availability Calendar'
                : 'Request Calendar Access'}
            </button>

            {(userData?.hasResume || userData?.resume) && (
              <button
                type="button"
                className="candidate-secondary-button candidate-resume-button"
                title="Open resume preview"
                onClick={() => onResumePreview?.()}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                Open Resume Preview
              </button>
            )}
          </div>
        </div>
      </div>

      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Profile Picture</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {userData?.profileImg ? (
            <img
              src={userData.profileImg}
              alt="Profile Preview"
              className="candidate-profile-modal-image"
            />
          ) : (
            <div>No image available</div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PublicUserPanel;
